import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Copy, Check, PartyPopper, RefreshCw, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const IntegrationDialog = ({ open, onOpenChange, projectId, isFirstPublish, apiKey, supabase, onUpdateProject }) => {
    const [copiedProjectId, setCopiedProjectId] = useState(false);
    const [copiedApiKey, setCopiedApiKey] = useState(false);
    const [copiedInstall, setCopiedInstall] = useState(false);
    const [copiedUsage, setCopiedUsage] = useState(false);
    const [copiedIosSetup, setCopiedIosSetup] = useState(false);
    const [copiedExpoSetup, setCopiedExpoSetup] = useState(false);
    const [copiedSwiftInstall, setCopiedSwiftInstall] = useState(false);
    const [copiedSwiftUsage, setCopiedSwiftUsage] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [showRotateConfirm, setShowRotateConfirm] = useState(false);

    useEffect(() => {
        if (open && isFirstPublish) {
            const duration = 3 * 1000;
            const animationEnd = Date.now() + duration;
            const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

            const randomInRange = (min, max) => Math.random() * (max - min) + min;

            const interval = setInterval(function () {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    return clearInterval(interval);
                }

                const particleCount = 50 * (timeLeft / duration);
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
            }, 250);

            return () => clearInterval(interval);
        }
    }, [open, isFirstPublish]);

    const handleCopy = (text, setStateFn) => {
        navigator.clipboard.writeText(text);
        setStateFn(true);
        setTimeout(() => setStateFn(false), 2000);
        toast.success("Copied to clipboard");
    };

    const handleRotateApiKey = async () => {
        try {
            setIsRotating(true);
            const newApiKey = crypto.randomUUID();

            const { error } = await supabase
                .from('projects')
                .update({ api_key: newApiKey })
                .eq('id', projectId);

            if (error) throw error;

            onUpdateProject({ api_key: newApiKey });
            toast.success("API Key rotated successfully");
            setShowRotateConfirm(false);
        } catch (error) {
            console.error('Error rotating API key:', error);
            toast.error("Failed to rotate API key");
        } finally {
            setIsRotating(false);
        }
    };

    const installCommand = `npm install react-native-pop-onboarding`;
    const iosSetupCode = `<key>NSAppTransportSecurity</key>
<dict>
    <key>NSAllowsArbitraryLoads</key>
    <true/>
</dict>`;

    const expoSetupCode = `{
  "expo": {
    "ios": {
      "infoPlist": {
        "NSAppTransportSecurity": {
          "NSAllowsArbitraryLoads": true
        }
      }
    }
  }
}`;

    const usageCode = `import { useOnboarding, PopOnboarding } from 'react-native-pop-onboarding';
import AsyncStorage from '@react-native-async-storage/async-storage';

const App = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Best Practice: Only show to first-time users
  useEffect(() => {
    const checkOnboarding = async () => {
      const hasSeenOnboarding = await AsyncStorage.getItem('onboarding_complete');
      if (!hasSeenOnboarding) {
        setShowOnboarding(true);
      }
    };
    checkOnboarding();
  }, []);

  const { projectExists, loading, error } = useOnboarding('${projectId}', {
    apiKey: '${apiKey || 'YOUR_PROJECT_API_KEY'}',
  });

  const handleComplete = async () => {
    await AsyncStorage.setItem('onboarding_complete', 'true');
    setShowOnboarding(false);
    console.log('Completed!');
  };

  if (!showOnboarding) return <MainApp />;
  if (loading) return <Text>Loading...</Text>;
  if (error || !projectExists) {
    // Gracefully handle errors - don't break the app
    console.error('Onboarding error:', error);
    return <MainApp />;
  }

  return (
    <PopOnboarding 
      projectId="${projectId}"
      apiKey="${apiKey || 'YOUR_PROJECT_API_KEY'}"
      onComplete={handleComplete}
      onSkip={handleComplete}
    />
  );
};`;

    const swiftUsageCode = `import PopOnboarding
import UIKit

// 1. Configure in AppDelegate
func application(_ application: UIApplication, didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?) -> Bool {
    PopOnboarding.shared.configure(apiKey: "${apiKey || 'YOUR_API_KEY'}")
    return true
}

// 2. Start Flow
func showOnboarding() {
    PopOnboarding.shared.startFlow(
        projectId: "${projectId}",
        from: self
    ) { result in
        if case .success(let data) = result {
            if data.completed {
                print("Onboarding completed!")
            }
        }
    }
}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        {isFirstPublish && <PartyPopper className="h-6 w-6 text-yellow-500" />}
                        {isFirstPublish ? "Congrats on your first onboarding!" : "Integrate Onboarding"}
                    </DialogTitle>
                    <DialogDescription>
                        {isFirstPublish
                            ? "You've successfully published your flow. Here's how to add it to your app."
                            : "Follow these steps to add this onboarding flow to your React Native app."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {/* Credentials Section */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold border-b pb-2">Credentials</h4>

                        <div className="grid gap-4">
                            <div className="space-y-2">
                                <Label>Project ID</Label>
                                <div className="flex gap-2">
                                    <Input value={projectId} readOnly className="font-mono bg-muted" />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleCopy(projectId, setCopiedProjectId)}
                                    >
                                        {copiedProjectId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>API Key</Label>
                                    {!showRotateConfirm ? (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-xs text-muted-foreground hover:text-destructive"
                                            onClick={() => setShowRotateConfirm(true)}
                                        >
                                            <RefreshCw className="h-3 w-3 mr-1" />
                                            Rotate Key
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-destructive font-medium">Are you sure?</span>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className="h-6 text-xs"
                                                onClick={handleRotateApiKey}
                                                disabled={isRotating}
                                            >
                                                {isRotating ? "Rotating..." : "Confirm"}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 text-xs"
                                                onClick={() => setShowRotateConfirm(false)}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        value={apiKey || 'Loading...'}
                                        readOnly
                                        type="password"
                                        className="font-mono bg-muted"
                                    />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleCopy(apiKey || '', setCopiedApiKey)}
                                        disabled={!apiKey}
                                    >
                                        {copiedApiKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                                {showRotateConfirm && (
                                    <Alert variant="destructive" className="py-2">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertDescription className="text-xs">
                                            Rotating the API key will invalidate the old one. Your app will stop working until you update it with the new key.
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Installation */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold border-b pb-2">Installation</h4>

                        <Tabs defaultValue="react-native" className="w-full">
                            <TabsList className="grid w-full grid-cols-2 mb-4">
                                <TabsTrigger value="react-native">React Native</TabsTrigger>
                                <TabsTrigger value="swift">Swift (iOS)</TabsTrigger>
                            </TabsList>

                            <TabsContent value="react-native" className="space-y-6">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs text-muted-foreground">1. Install the SDK</Label>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-xs"
                                            onClick={() => handleCopy(installCommand, setCopiedInstall)}
                                        >
                                            {copiedInstall ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                            Copy
                                        </Button>
                                    </div>
                                    <SyntaxHighlighter
                                        language="bash"
                                        style={vscDarkPlus}
                                        wrapLongLines={true}
                                        customStyle={{
                                            margin: 0,
                                            borderRadius: '0.5rem',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        {installCommand}
                                    </SyntaxHighlighter>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs text-muted-foreground">2. iOS Setup (Required for Production)</Label>
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">Important</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        Configure iOS to allow dynamic content loading in production builds:
                                    </p>
                                    <Tabs defaultValue="expo" className="w-full">
                                        <TabsList className="grid w-full grid-cols-2">
                                            <TabsTrigger value="expo">Expo</TabsTrigger>
                                            <TabsTrigger value="bare">Bare React Native</TabsTrigger>
                                        </TabsList>
                                        <TabsContent value="expo" className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs text-muted-foreground">
                                                    Add this to your <code className="bg-muted px-1 py-0.5 rounded">app.json</code> or <code className="bg-muted px-1 py-0.5 rounded">app.config.js</code>:
                                                </p>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 text-xs"
                                                    onClick={() => handleCopy(expoSetupCode, setCopiedExpoSetup)}
                                                >
                                                    {copiedExpoSetup ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                                    Copy
                                                </Button>
                                            </div>
                                            <SyntaxHighlighter
                                                language="json"
                                                style={vscDarkPlus}
                                                wrapLongLines={true}
                                                customStyle={{
                                                    margin: 0,
                                                    borderRadius: '0.5rem',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {expoSetupCode}
                                            </SyntaxHighlighter>
                                        </TabsContent>
                                        <TabsContent value="bare" className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs text-muted-foreground">
                                                    Add this to your <code className="bg-muted px-1 py-0.5 rounded">ios/YourApp/Info.plist</code>:
                                                </p>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 text-xs"
                                                    onClick={() => handleCopy(iosSetupCode, setCopiedIosSetup)}
                                                >
                                                    {copiedIosSetup ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                                    Copy
                                                </Button>
                                            </div>
                                            <SyntaxHighlighter
                                                language="xml"
                                                style={vscDarkPlus}
                                                wrapLongLines={true}
                                                customStyle={{
                                                    margin: 0,
                                                    borderRadius: '0.5rem',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {iosSetupCode}
                                            </SyntaxHighlighter>
                                        </TabsContent>
                                    </Tabs>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs text-muted-foreground">3. Add to your App</Label>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-xs"
                                            onClick={() => handleCopy(usageCode, setCopiedUsage)}
                                        >
                                            {copiedUsage ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                            Copy
                                        </Button>
                                    </div>
                                    <SyntaxHighlighter
                                        language="javascript"
                                        style={vscDarkPlus}
                                        wrapLongLines={true}
                                        customStyle={{
                                            margin: 0,
                                            borderRadius: '0.5rem',
                                            fontSize: '0.875rem',
                                            maxHeight: '400px'
                                        }}
                                        showLineNumbers
                                    >
                                        {usageCode}
                                    </SyntaxHighlighter>
                                </div>
                            </TabsContent>

                            <TabsContent value="swift" className="space-y-6">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs text-muted-foreground">1. Add Package Dependency</Label>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-xs"
                                            onClick={() => handleCopy("https://github.com/jamesagib/pop-onboarding-swift", setCopiedSwiftInstall)}
                                        >
                                            {copiedSwiftInstall ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                            Copy URL
                                        </Button>
                                    </div>
                                    <div className="p-3 bg-muted rounded-md text-sm font-mono break-all">
                                        https://github.com/jamesagib/pop-onboarding-swift
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        In Xcode: File → Add Package Dependencies → Paste URL
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs text-muted-foreground">2. Usage</Label>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-xs"
                                            onClick={() => handleCopy(swiftUsageCode, setCopiedSwiftUsage)}
                                        >
                                            {copiedSwiftUsage ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                            Copy
                                        </Button>
                                    </div>
                                    <SyntaxHighlighter
                                        language="swift"
                                        style={vscDarkPlus}
                                        wrapLongLines={true}
                                        customStyle={{
                                            margin: 0,
                                            borderRadius: '0.5rem',
                                            fontSize: '0.875rem',
                                            maxHeight: '400px'
                                        }}
                                        showLineNumbers
                                    >
                                        {swiftUsageCode}
                                    </SyntaxHighlighter>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default IntegrationDialog;
